// Error grouping ("incidências") - mirrors the logic in frontend-company's
// IntegrationsErrorsPage.jsx, using the same API calls. Keep both in sync.
import api from './api'
import { getPeriodDateRange } from './statsPeriod'

// error_message is str(error) + "\n\n" + traceback.format_exc() - older rows
// can carry a trailing "NoneType: None" artifact (no active exception when the
// failure was recorded), so strip it only when trailing.
export const getTraceError = (trace) => {
  const failedStep = trace.steps?.find((s) => s.status === 'failed' && s.error_message)
  if (!failedStep?.error_message) return null
  const raw = String(failedStep.error_message)
  const cleaned = raw.replace(/(\s*NoneType:\s*None\s*)+$/i, '').trim()
  return cleaned || null
}

// Shape-only "signature" for grouping - "AN: 1234" and "AN: 5678" both become
// "AN: #". Heuristic (digits only), not a real templating engine.
export const buildErrorSignature = (text) => text.replace(/\d+/g, '#').replace(/\s+/g, ' ').trim()

const MAX_PAGES = 20 // up to 2000 failed traces scanned (same as desktop)

// Fetches every failed trace of the integration within the period (paginating
// past the API's 100-per-page cap, up to MAX_PAGES) and groups them by
// signature, most frequent first.
export const fetchIncidenceSummary = async (companyId, integrationId, period) => {
  const { dateFrom, dateTo } = getPeriodDateRange(period)
  const baseParams = { page_size: 100, status: 'failed' }
  if (dateFrom) baseParams.date_from = dateFrom
  if (dateTo) baseParams.date_to = dateTo

  let allItems = []
  let total = 0
  let pageNum = 1
  for (;;) {
    const response = await api.get(
      `/api/event-traces/company/${companyId}/integration/${integrationId}`,
      { params: { ...baseParams, page: pageNum } }
    )
    const items = response.data.items || []
    total = response.data.total || 0
    allItems = allItems.concat(items)
    if (items.length === 0 || allItems.length >= total || pageNum >= MAX_PAGES) break
    pageNum += 1
  }

  const groupsByKey = new Map()
  allItems.forEach((trace) => {
    const err = getTraceError(trace)
    if (!err) return
    const signature = buildErrorSignature(err)
    const key = signature.toLowerCase()
    if (!groupsByKey.has(key)) groupsByKey.set(key, { signature, count: 0, refs: [] })
    const group = groupsByKey.get(key)
    group.count += 1
    // Only what /reprocess needs (see reprocessGroup) - not the whole trace.
    group.refs.push({ traceId: trace.id, eventId: trace.cloud_event_id })
  })

  return {
    groups: Array.from(groupsByKey.values()).sort((a, b) => b.count - a.count),
    scanned: allItems.length,
    total
  }
}

const REPROCESS_BATCH_SIZE = 50
// The backend handles each event sequentially (payload fetch from object
// storage + enqueue), so a 50-event batch can take well past the default 12s.
const REPROCESS_BATCH_TIMEOUT = 60000

// Same call as frontend-company's EventsTracePage bulk reprocess
// (POST /api/cloud-event-logs/reprocess), split into batches so a large group
// doesn't hit the request timeout and the UI can report progress.
// Stops early when a batch is fully rejected (count 0) - most likely the
// integration is inactive, and the backend would log one extra failure per
// event sent. Returns { sent, accepted, stoppedEarly }; on a network/server
// error the thrown error carries `accepted` (events enqueued before it).
export const reprocessGroup = async (refs, onProgress) => {
  let sent = 0
  let accepted = 0
  for (let i = 0; i < refs.length; i += REPROCESS_BATCH_SIZE) {
    const batch = refs.slice(i, i + REPROCESS_BATCH_SIZE)
    let response
    try {
      response = await api.post(
        '/api/cloud-event-logs/reprocess',
        { event_ids: batch.map((r) => r.eventId), trace_ids: batch.map((r) => r.traceId) },
        { timeout: REPROCESS_BATCH_TIMEOUT }
      )
    } catch (error) {
      error.accepted = accepted
      throw error
    }
    const count = response.data?.count || 0
    sent += batch.length
    accepted += count
    onProgress?.(sent, refs.length)
    if (count === 0) return { sent, accepted, stoppedEarly: sent < refs.length }
  }
  return { sent, accepted, stoppedEarly: false }
}
